import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DataDeletion() {
  const { lang } = useLang();
  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="data-deletion-page">
      <header className="px-6 md:px-12 lg:px-16 py-6 border-b border-[var(--border)]">
        <Link to="/" className="label-cap" data-testid="data-deletion-back-home">— Mordomo</Link>
      </header>
      <article className="px-6 md:px-12 lg:px-16 py-12 md:py-20 max-w-3xl">
        <p className="label-cap">— {lang === "pt" ? "Documento legal" : "Legal"}</p>
        <h1
          className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3"
          data-testid="data-deletion-title"
        >
          {lang === "pt" ? "Eliminação de Dados" : "Data Deletion"}
        </h1>
        <p className="font-display-italic text-[var(--ink-soft)] mt-3">
          {lang === "pt" ? "Versão de 17 de Junho de 2026" : "Version dated June 17, 2026"}
        </p>

        <TransparencyStrip lang={lang} />

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed">
          {lang === "pt" ? <ContentPT /> : <ContentEN />}
        </div>

        <DeletionRequestForm lang={lang} />

        <div className="mt-16 pt-6 border-t border-[var(--border)] flex gap-6 text-sm">
          <Link
            to="/privacy"
            className="label-cap hover:text-[var(--oxblood)]"
            data-testid="data-deletion-to-privacy"
          >
            {lang === "pt" ? "Política de Privacidade" : "Privacy Policy"}
          </Link>
          <Link
            to="/terms"
            className="label-cap hover:text-[var(--oxblood)]"
            data-testid="data-deletion-to-terms"
          >
            {lang === "pt" ? "Termos de Serviço" : "Terms of Service"}
          </Link>
          <Link to="/" className="label-cap hover:text-[var(--oxblood)]" data-testid="data-deletion-to-home">
            {lang === "pt" ? "Voltar à página inicial" : "Back to home"}
          </Link>
        </div>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-light mt-8 mb-3 text-[var(--ink)]">{title}</h2>
      {children}
    </section>
  );
}

function ContentPT() {
  return (
    <>
      <Section title="1. O direito de ser esquecida">
        <p>
          Ao abrigo do RGPD (artigo 17.º) e da nossa Política de Privacidade, qualquer Chefe pode pedir,
          a qualquer momento, a eliminação completa dos seus dados pessoais do Mordomo — sem
          justificações, sem formulários longos, sem demoras.
        </p>
      </Section>

      <Section title="2. Como pedir a eliminação">
        <p>Há três caminhos. Escolha o que lhe for mais cómodo:</p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li>
            <strong>Pela aplicação</strong>: entre na sua conta e vá a{" "}
            <em>Definições → Conta → Eliminar conta</em>. O processo confirma com duas perguntas e
            executa em minutos.
          </li>
          <li>
            <strong>Por WhatsApp</strong>: envie a palavra{" "}
            <code>APAGAR</code> para o número Mordomo. Pedimos confirmação e tratamos do resto.
          </li>
          <li>
            <strong>Por email</strong>: escreva para{" "}
            <a href="mailto:privacidade@my-butler.ai" className="text-[var(--oxblood)] underline">
              privacidade@my-butler.ai
            </a>{" "}
            a partir do endereço da conta. Respondemos em 24 horas úteis e confirmamos por escrito
            quando concluído.
          </li>
        </ul>
      </Section>

      <Section title="3. O que é apagado">
        <ul className="list-disc pl-6 space-y-2">
          <li>Perfil, email, palavra-passe e todos os campos de identificação.</li>
          <li>Histórico de conversas com o Mordomo (chat e WhatsApp).</li>
          <li>Memória multimodal (recibos, notas, fotos enviadas).</li>
          <li>Acordos digitais (mantemos só o nome do(a) filho(a) em registo anonimizado se houver factura associada).</li>
          <li>Tokens OAuth do Gmail (apagados imediatamente; revogamos também no Google).</li>
          <li>Número de WhatsApp associado.</li>
          <li>Diário semanal e snapshots.</li>
        </ul>
      </Section>

      <Section title="4. O que pode ficar (por obrigação legal)">
        <p>
          Apenas registos contabilísticos mínimos exigidos pela lei portuguesa: facturas e recibos
          de pagamento, pelo período de 10 anos. Estes ficam pseudonimizados — sem o seu nome
          completo, apenas com o NIF (se foi fornecido) e o valor.
        </p>
      </Section>

      <Section title="5. Prazos">
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Acção imediata</strong>: dentro de 24 horas úteis após a confirmação.</li>
          <li><strong>Limite máximo legal</strong>: 30 dias (RGPD artigo 12.º).</li>
          <li><strong>Confirmação por escrito</strong>: enviamos email a confirmar a eliminação.</li>
        </ul>
      </Section>

      <Section title="6. Antes de apagar — quer só uma pausa?">
        <p>
          Se está apenas a precisar de tempo, pode <strong>cancelar a subscrição</strong> sem apagar
          a conta — os dados ficam connosco 90 dias e pode voltar quando quiser. Para eliminação
          definitiva, siga as instruções da secção 2.
        </p>
      </Section>

      <Section title="7. Contacto directo">
        <p>
          Responsável pelo tratamento: Mordomo Lda.
          <br />
          Email:{" "}
          <a href="mailto:privacidade@my-butler.ai" className="text-[var(--oxblood)] underline">
            privacidade@my-butler.ai
          </a>
          <br />
          Autoridade de controlo: Comissão Nacional de Protecção de Dados (CNPD).
        </p>
      </Section>
    </>
  );
}

function ContentEN() {
  return (
    <>
      <Section title="1. The right to be forgotten">
        <p>
          Under GDPR (Article 17) and our Privacy Policy, every Chefe can request complete deletion
          of her personal data at any time — no justification required, no long forms, no delays.
        </p>
      </Section>

      <Section title="2. How to request deletion">
        <p>Three paths. Pick whichever is most convenient:</p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li>
            <strong>In-app</strong>: sign in and go to <em>Settings → Account → Delete account</em>.
          </li>
          <li>
            <strong>WhatsApp</strong>: send the word <code>DELETE</code> to the Mordomo number. We
            confirm and handle the rest.
          </li>
          <li>
            <strong>Email</strong>: write to{" "}
            <a href="mailto:privacidade@my-butler.ai" className="text-[var(--oxblood)] underline">
              privacidade@my-butler.ai
            </a>{" "}
            from the account address. We reply within 24 business hours.
          </li>
        </ul>
      </Section>

      <Section title="3. What gets deleted">
        <p>
          Profile, credentials, all conversations (chat + WhatsApp), multimodal memory, digital
          agreements, Gmail OAuth tokens (revoked at Google), WhatsApp phone, weekly diary
          snapshots.
        </p>
      </Section>

      <Section title="4. What may remain (legal obligation)">
        <p>
          Minimal accounting records required by Portuguese law: payment invoices, kept for 10
          years, pseudonymised (only tax ID and amount).
        </p>
      </Section>

      <Section title="5. Timing">
        <p>
          Within 24 business hours after confirmation. Statutory maximum: 30 days. Written
          confirmation via email.
        </p>
      </Section>

      <Section title="6. Just want a break?">
        <p>
          You can cancel the subscription without deleting the account — data is kept for 90 days
          and you can return at any time.
        </p>
      </Section>

      <Section title="7. Direct contact">
        <p>
          Data controller: Mordomo Lda. — Email:{" "}
          <a href="mailto:privacidade@my-butler.ai" className="text-[var(--oxblood)] underline">
            privacidade@my-butler.ai
          </a>
          . Supervisory authority: Portuguese Data Protection Authority (CNPD).
        </p>
      </Section>
    </>
  );
}


function DeletionRequestForm({ lang }) {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [state, setState] = useState({ loading: false, error: "", success: null });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "", success: null });
    try {
      const r = await fetch(`${BACKEND_URL}/api/legal/data-deletion-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), reason: reason.trim() || null, lang }),
      });
      const body = await r.json();
      if (!r.ok) {
        const detail = Array.isArray(body?.detail) ? body.detail[0]?.msg : body?.detail;
        throw new Error(detail || (lang === "pt" ? "Pedido falhou. Tente outra vez." : "Request failed. Try again."));
      }
      setState({ loading: false, error: "", success: body });
      setEmail("");
      setReason("");
    } catch (err) {
      setState({ loading: false, error: err.message, success: null });
    }
  };

  if (state.success) {
    return (
      <div className="mt-16 p-8 border border-[var(--ink)] bg-[var(--bg-soft)]" data-testid="deletion-success">
        <p className="label-cap text-[var(--oxblood)]">— {lang === "pt" ? "Pedido recebido" : "Request received"}</p>
        <p className="font-display text-2xl font-light mt-3">
          {lang === "pt" ? "Obrigada pela sua confiança." : "Thank you for trusting us."}
        </p>
        <p className="mt-4 text-[15px] leading-relaxed">{state.success.message}</p>
        <p className="mt-3 text-sm text-[var(--ink-soft)]">
          {lang === "pt" ? "Referência: " : "Reference: "}
          <code data-testid="deletion-success-ref">{state.success.request_id}</code>
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-16 p-8 border border-[var(--border)] bg-[var(--bg-soft)] space-y-5"
      data-testid="deletion-form"
    >
      <div>
        <p className="label-cap text-[var(--oxblood)]">— {lang === "pt" ? "Pedido formal" : "Formal request"}</p>
        <h2 className="font-display text-2xl font-light mt-2">
          {lang === "pt" ? "Pedir eliminação agora" : "Request deletion now"}
        </h2>
        <p className="mt-2 text-sm text-[var(--ink-soft)]">
          {lang === "pt"
            ? "Preencha o seu email da conta. Enviamos-lhe uma confirmação por escrito e tratamos do assunto dentro de 24 horas úteis."
            : "Use the email of your account. We send a written confirmation and action it within 24 business hours."}
        </p>
      </div>

      <div>
        <label className="label-cap block mb-2" htmlFor="deletion-email">
          {lang === "pt" ? "Email" : "Email"}
        </label>
        <input
          id="deletion-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={lang === "pt" ? "chefe@exemplo.com" : "chefe@example.com"}
          className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-3 text-[15px] focus:outline-none focus:border-[var(--ink)]"
          data-testid="deletion-email-input"
        />
      </div>

      <div>
        <label className="label-cap block mb-2" htmlFor="deletion-reason">
          {lang === "pt" ? "Motivo (opcional)" : "Reason (optional)"}
        </label>
        <textarea
          id="deletion-reason"
          rows={3}
          maxLength={2000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={lang === "pt" ? "Se quiser dizer porquê — sem obrigação." : "If you wish to share why — no obligation."}
          className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-3 text-[15px] focus:outline-none focus:border-[var(--ink)]"
          data-testid="deletion-reason-input"
        />
      </div>

      {state.error && (
        <p className="text-sm text-[var(--oxblood)]" data-testid="deletion-error">{state.error}</p>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--ink-faint)]">
          {lang === "pt"
            ? "Ao submeter aceita que tratemos este pedido nos termos da Política de Privacidade."
            : "By submitting you accept this is processed per our Privacy Policy."}
        </p>
        <button
          type="submit"
          disabled={state.loading || !email}
          className="btn-primary text-sm disabled:opacity-50"
          data-testid="deletion-submit"
        >
          {state.loading
            ? (lang === "pt" ? "A enviar…" : "Sending…")
            : (lang === "pt" ? "Confirmar pedido" : "Confirm request")}
        </button>
      </div>
    </form>
  );
}

function TransparencyStrip({ lang }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/legal/transparency`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => { if (!cancelled) setData(body); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;

  const labels = lang === "pt"
    ? {
        title: "Transparência",
        intro: "Últimos 30 dias:",
        received: "Pedidos recebidos",
        completed: "Pedidos concluídos",
        avg: "Tempo médio até eliminação",
        grace: "Margem antes da execução",
        hours: "horas",
        none: "—",
      }
    : {
        title: "Transparency",
        intro: "Last 30 days:",
        received: "Requests received",
        completed: "Requests completed",
        avg: "Avg time to deletion",
        grace: "Grace before action",
        hours: "hours",
        none: "—",
      };

  return (
    <div className="mt-10 border border-[var(--border)] px-6 py-5 bg-[var(--bg-soft)]" data-testid="transparency-strip">
      <p className="label-cap text-[var(--oxblood)]">— {labels.title}</p>
      <p className="text-sm text-[var(--ink-soft)] mt-1">{labels.intro}</p>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-5 text-sm">
        <Stat label={labels.received} value={data.deletion_requests_received} testid="t-received" />
        <Stat label={labels.completed} value={data.deletion_requests_completed} testid="t-completed" />
        <Stat
          label={labels.avg}
          value={data.avg_hours_to_completion != null ? `${data.avg_hours_to_completion} ${labels.hours}` : labels.none}
          testid="t-avg"
        />
        <Stat label={labels.grace} value={`${data.grace_hours} ${labels.hours}`} testid="t-grace" />
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <p className="label-cap text-[var(--ink-faint)]">{label}</p>
      <p className="font-display text-2xl font-light mt-1 break-words">{value}</p>
    </div>
  );
}

